import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { CalendarIcon, ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { formatAvailabilityHours, getWeeklyAvailabilityMinutes } from '~/lib/availability-duration';
import { StoreSelector } from '~/pages/AppointmentsCalendar';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import { CategoryScheduleEntryForm } from './CategoryScheduleEntryForm';
import { ShiftPlanEntryForm } from './ShiftPlanEntryForm';

dayjs.extend(isoWeek);

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const CATEGORY_COLORS = [
    'bg-emerald-50',
    'bg-purple-50',
    'bg-blue-50',
    'bg-orange-100',
    'bg-amber-50',
    'bg-rose-50',
    'bg-cyan-50',
    'bg-indigo-50',
];

const BLOCKER_TYPE_LABELS: Record<string, string> = {
    BREAK: 'Pause',
    VACATION: 'Urlaub',
    MEETING: 'Meeting',
    SICK_LEAVE: 'Krank',
    EDUCATION: 'Fortbildung',
    OTHER: 'Sonstiges',
};

type DraftPublishSummary = {
    entryId: string;
    practitionerName: string;
    categoryName: string;
    kind: 'Verfügbarkeit' | 'Blocker';
    blockerType?: string | null;
    date: string;
    startTime: string;
    endTime: string;
    isRecurring: boolean;
    recurrenceRule: string | null;
    recurrenceEndDate: string | null;
};

const formatDraftOccurrenceDate = (date: string) => {
    const parsedDate = dayjs(date);
    const dayLabel = DAY_LABELS[parsedDate.isoWeekday() - 1] ?? parsedDate.format('dd');
    return `${dayLabel}, ${parsedDate.format('DD.MM.')}`;
};

const RECURRENCE_FREQUENCY_LABELS: Record<string, string> = {
    DAILY: 'täglich',
    WEEKLY: 'wöchentlich',
    MONTHLY: 'monatlich',
};

const RECURRENCE_WEEKDAY_LABELS: Record<string, string> = {
    MO: 'Mo',
    TU: 'Di',
    WE: 'Mi',
    TH: 'Do',
    FR: 'Fr',
    SA: 'Sa',
    SU: 'So',
};

const formatDraftSchedule = (summary: DraftPublishSummary) => {
    const baseDate = `${formatDraftOccurrenceDate(summary.date)} ${summary.startTime} - ${summary.endTime}`;
    if (!summary.isRecurring || !summary.recurrenceRule) return baseDate;

    const ruleParts = Object.fromEntries(
        summary.recurrenceRule.split(';').map(part => {
            const [key, value] = part.split('=');
            return [key, value];
        })
    );
    const frequency = RECURRENCE_FREQUENCY_LABELS[ruleParts.FREQ ?? ''] ?? 'wiederholt';
    const interval = ruleParts.INTERVAL && ruleParts.INTERVAL !== '1' ? `alle ${ruleParts.INTERVAL} ` : '';
    const weekdays = ruleParts.BYDAY
        ? ` (${ruleParts.BYDAY.split(',').map(day => RECURRENCE_WEEKDAY_LABELS[day] ?? day).join(', ')})`
        : '';
    const recurrenceEnd = summary.recurrenceEndDate ? ` bis ${dayjs(summary.recurrenceEndDate).format('DD.MM.YYYY')}` : '';

    return `${baseDate}, ${interval}${frequency}${weekdays}${recurrenceEnd}`;
};

export function ShiftPlanDraftTab() {
    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow(state => ({ selectedStore: state.selectedStore }))
    );
    const userRole = useUserStore(state => state.user?.role);
    const isAdmin = userRole === UserRole.ADMIN;

    const [currentWeekStart, setCurrentWeekStart] = useState(() => dayjs().startOf('isoWeek'));
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
    const [editingDate, setEditingDate] = useState<string | undefined>(undefined);
    const [prefillPractitionerId, setPrefillPractitionerId] = useState<number | undefined>(undefined);
    const [prefillDate, setPrefillDate] = useState<string | undefined>(undefined);
    const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
    const [editingCategoryEntryId, setEditingCategoryEntryId] = useState<string | null>(null);
    const [editingCategoryDate, setEditingCategoryDate] = useState<string | undefined>(undefined);
    const [categoryFormCategoryId, setCategoryFormCategoryId] = useState<number | null>(null);
    const [categoryFormCategoryName, setCategoryFormCategoryName] = useState<string>('');
    const [categoryFormPrefillDate, setCategoryFormPrefillDate] = useState<string | undefined>(undefined);
    const [isPublishDraftsDialogOpen, setIsPublishDraftsDialogOpen] = useState(false);

    const handlePrevWeek = () => setCurrentWeekStart(prev => prev.subtract(7, 'day'));
    const handleNextWeek = () => setCurrentWeekStart(prev => prev.add(7, 'day'));
    const handleTodayClick = () => setCurrentWeekStart(dayjs().startOf('isoWeek'));

    const daysOfWeek = useMemo(
        () => Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, 'day')),
        [currentWeekStart]
    );

    const weekEndDisplay = currentWeekStart.add(6, 'day');
    const weekNumber = currentWeekStart.isoWeek();

    const utils = trpc.useUtils();
    const publishManyDraftsMutation = trpc.shiftPlanEntries.publishMany.useMutation();

    const { data, isLoading } = trpc.practitioners.getStoreWeeklyScheduleOverview.useQuery(
        { storeId: selectedStore?.id ?? 0, weekStart: currentWeekStart.format('YYYY-MM-DD'), includeDrafts: true },
        { enabled: !!selectedStore }
    );

    const { data: storeDrafts, isLoading: isLoadingStoreDrafts } = trpc.shiftPlanEntries.getStoreDrafts.useQuery(
        { storeId: selectedStore?.id ?? 0 },
        { enabled: !!selectedStore && isAdmin }
    );

    const { data: preferences } = trpc.practitionerAvailabilityPreferences.getStoreWeek.useQuery(
        { storeId: selectedStore?.id ?? 0, weekStart: currentWeekStart.format('YYYY-MM-DD') },
        { enabled: !!selectedStore }
    );

    const { data: categoryEntries } = trpc.categoryScheduleEntries.getStoreWeek.useQuery(
        { storeId: selectedStore?.id ?? 0, weekStart: currentWeekStart.format('YYYY-MM-DD') },
        { enabled: !!selectedStore }
    );

    const categoryEntriesMap = useMemo(() => {
        const map: Record<number, Record<string, { startTime: string; endTime: string; entryId: string; description: string | null }[]>> = {};
        if (!categoryEntries) return map;
        for (const entry of categoryEntries) {
            if (!map[entry.categoryId]) map[entry.categoryId] = {};
            if (!map[entry.categoryId][entry.date]) map[entry.categoryId][entry.date] = [];
            map[entry.categoryId][entry.date].push({
                startTime: entry.startTime,
                endTime: entry.endTime,
                entryId: entry.entryId,
                description: entry.description,
            });
        }
        return map;
    }, [categoryEntries]);

    const preferencesMap = useMemo(() => {
        const map: Record<number, Record<string, { startTime: string; endTime: string; type: string }>> = {};
        if (!preferences) return map;
        for (const pref of preferences) {
            if (!map[pref.practitionerId]) {
                map[pref.practitionerId] = {};
            }
            map[pref.practitionerId][pref.date] = { startTime: pref.startTime, endTime: pref.endTime, type: pref.type };
        }
        return map;
    }, [preferences]);

    const groupedPractitioners = useMemo(() => {
        if (!data?.practitioners) return [];

        const groups: { categoryId: number | null; categoryName: string; practitioners: typeof data.practitioners }[] = [];
        const categoryMap = new Map<number | null, typeof data.practitioners>();

        for (const p of data.practitioners) {
            const catId = p.category?.id ?? null;
            if (!categoryMap.has(catId)) {
                categoryMap.set(catId, []);
            }
            categoryMap.get(catId)!.push(p);
        }

        for (const [catId, practitioners] of categoryMap) {
            const categoryName = practitioners[0]?.category?.name ?? 'Ohne Kategorie';
            groups.push({ categoryId: catId, categoryName, practitioners });
        }

        groups.sort((a, b) => {
            if (a.categoryId === null) return 1;
            if (b.categoryId === null) return -1;
            return a.categoryId - b.categoryId;
        });

        return groups;
    }, [data]);

    const draftPublishSummaries = useMemo(() => {
        return (storeDrafts ?? [])
            .map(draft => ({
                entryId: draft.entryId,
                practitionerName: draft.practitionerName,
                categoryName: draft.categoryName,
                kind: draft.type === 'AVAILABLE' ? 'Verfügbarkeit' as const : 'Blocker' as const,
                blockerType: draft.blockedType,
                date: draft.date,
                startTime: draft.startTime,
                endTime: draft.endTime,
                isRecurring: draft.isRecurring,
                recurrenceRule: draft.recurrenceRule,
                recurrenceEndDate: draft.recurrenceEndDate,
            }))
            .sort((a, b) => {
                const categoryCompare = a.categoryName.localeCompare(b.categoryName);
                if (categoryCompare !== 0) return categoryCompare;
                const practitionerCompare = a.practitionerName.localeCompare(b.practitionerName);
                if (practitionerCompare !== 0) return practitionerCompare;
                return `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`);
            });
    }, [storeDrafts]);

    const handlePublishAllDrafts = async () => {
        if (!isAdmin || !selectedStore || draftPublishSummaries.length === 0) return;

        try {
            const result = await publishManyDraftsMutation.mutateAsync({
                storeId: selectedStore.id,
                ids: draftPublishSummaries.map(summary => summary.entryId),
            });
            toast.success('Entwürfe veröffentlicht', {
                description: `${result.count} Entwurf${result.count === 1 ? '' : 'e'} für ${selectedStore.label} veröffentlicht.`,
            });
            setIsPublishDraftsDialogOpen(false);
            if (isAdmin) {
                utils.shiftPlanEntries.getStoreDrafts.invalidate({ storeId: selectedStore.id });
            }
            utils.practitioners.getStoreWeeklyScheduleOverview.invalidate({
                storeId: selectedStore.id,
                weekStart: currentWeekStart.format('YYYY-MM-DD'),
                includeDrafts: true,
            });
        } catch (e) {
            console.error('Error publishing draft entries:', e);
            toast.error('Entwürfe konnten nicht veröffentlicht werden.');
        }
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setEditingEntryId(null);
        setEditingDate(undefined);
        setPrefillPractitionerId(undefined);
        setPrefillDate(undefined);
    };
    const handleFormSuccess = () => {
        setIsFormOpen(false);
        setEditingEntryId(null);
        setEditingDate(undefined);
        setPrefillPractitionerId(undefined);
        setPrefillDate(undefined);
        utils.practitioners.getStoreWeeklyScheduleOverview.invalidate();
        if (selectedStore && isAdmin) {
            utils.shiftPlanEntries.getStoreDrafts.invalidate({ storeId: selectedStore.id });
        }
    };
    const handleEditEntry = (entryId: string, date: string) => {
        setEditingEntryId(entryId);
        setEditingDate(date);
        setPrefillPractitionerId(undefined);
        setPrefillDate(undefined);
        setIsFormOpen(true);
    };
    const handleCreateEntry = (practitionerId: number, date: string) => {
        setEditingEntryId(null);
        setEditingDate(undefined);
        setPrefillPractitionerId(practitionerId);
        setPrefillDate(date);
        setIsFormOpen(true);
    };

    const handleCategoryFormClose = () => {
        setIsCategoryFormOpen(false);
        setEditingCategoryEntryId(null);
        setEditingCategoryDate(undefined);
        setCategoryFormCategoryId(null);
        setCategoryFormCategoryName('');
        setCategoryFormPrefillDate(undefined);
    };
    const handleCategoryFormSuccess = () => {
        setIsCategoryFormOpen(false);
        setEditingCategoryEntryId(null);
        setEditingCategoryDate(undefined);
        setCategoryFormCategoryId(null);
        setCategoryFormCategoryName('');
        setCategoryFormPrefillDate(undefined);
        utils.categoryScheduleEntries.getStoreWeek.invalidate();
    };
    const handleEditCategoryEntry = (entryId: string, categoryId: number, categoryName: string, date: string) => {
        setEditingCategoryEntryId(entryId);
        setEditingCategoryDate(date);
        setCategoryFormCategoryId(categoryId);
        setCategoryFormCategoryName(categoryName);
        setCategoryFormPrefillDate(undefined);
        setIsCategoryFormOpen(true);
    };
    const handleCreateCategoryEntry = (categoryId: number, categoryName: string, date: string) => {
        setEditingCategoryEntryId(null);
        setEditingCategoryDate(undefined);
        setCategoryFormCategoryId(categoryId);
        setCategoryFormCategoryName(categoryName);
        setCategoryFormPrefillDate(date);
        setIsCategoryFormOpen(true);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <StoreSelector />
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleTodayClick}>
                        Heute
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleNextWeek}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium ml-2">
                        KW {weekNumber}, {currentWeekStart.format('DD.MM.')} - {weekEndDisplay.format('DD.MM.YYYY')}
                    </span>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="ml-2">
                                <CalendarIcon className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={currentWeekStart.toDate()}
                                onSelect={(date) => {
                                    if (date) setCurrentWeekStart(dayjs(date).startOf('isoWeek'));
                                }}
                                weekStartsOn={1}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {!selectedStore ? (
                <div className="flex items-center justify-center h-64">
                    <p className="text-muted-foreground">Bitte wählen Sie eine Filiale aus.</p>
                </div>
            ) : isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-auto max-h-[calc(100vh-220px)]">
                        <table className="w-full border-collapse min-w-[800px]">
                            <thead>
                                <tr>
                                    <th className="sticky top-0 z-10 bg-gray-100 text-left px-4 py-3 font-medium text-sm text-muted-foreground w-[180px] border-b-2 border-gray-300">
                                        Behandler
                                    </th>
                                    {daysOfWeek.map((day, i) => (
                                        <th key={i} className="sticky top-0 z-10 bg-gray-100 text-center px-2 py-3 font-medium text-sm text-muted-foreground border-b-2 border-l border-gray-300">
                                            {DAY_LABELS[i]} {day.format('DD.MM.')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groupedPractitioners.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                            Keine Behandler in dieser Filiale gefunden.
                                        </td>
                                    </tr>
                                )}
                                {groupedPractitioners.map((group, groupIdx) => (
                                    <DraftCategoryGroup
                                        key={group.categoryId ?? 'uncategorized'}
                                        group={group}
                                        isFirst={groupIdx === 0}
                                        weekStart={currentWeekStart.format('YYYY-MM-DD')}
                                        colorIndex={groupIdx}
                                        preferencesMap={preferencesMap}
                                        onEditEntry={handleEditEntry}
                                        onCreateEntry={handleCreateEntry}
                                        categoryEntriesMap={categoryEntriesMap}
                                        onEditCategoryEntry={handleEditCategoryEntry}
                                        onCreateCategoryEntry={handleCreateCategoryEntry}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {isAdmin && (
                        <div className="flex justify-end">
                            <Button
                                onClick={() => setIsPublishDraftsDialogOpen(true)}
                                disabled={isLoadingStoreDrafts || draftPublishSummaries.length === 0 || publishManyDraftsMutation.isPending}
                            >
                                {(isLoadingStoreDrafts || publishManyDraftsMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Alle Entwürfe veröffentlichen
                                {draftPublishSummaries.length > 0 && ` (${draftPublishSummaries.length})`}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {isFormOpen && selectedStore && (
                <ShiftPlanEntryForm
                    key={editingEntryId ?? 'new'}
                    storeId={selectedStore.id}
                    open={isFormOpen}
                    onClose={handleFormClose}
                    onSuccess={handleFormSuccess}
                    editEntryId={editingEntryId ?? undefined}
                    prefillPractitionerId={prefillPractitionerId}
                    prefillDate={prefillDate}
                    selectedDay={editingDate}
                />
            )}

            {isCategoryFormOpen && selectedStore && categoryFormCategoryId && (
                <CategoryScheduleEntryForm
                    key={editingCategoryEntryId ?? 'new'}
                    storeId={selectedStore.id}
                    categoryId={categoryFormCategoryId}
                    categoryName={categoryFormCategoryName}
                    open={isCategoryFormOpen}
                    onClose={handleCategoryFormClose}
                    onSuccess={handleCategoryFormSuccess}
                    editEntryId={editingCategoryEntryId ?? undefined}
                    prefillDate={categoryFormPrefillDate}
                    selectedDay={editingCategoryDate}
                />
            )}

            {isAdmin && (
                <Dialog open={isPublishDraftsDialogOpen} onOpenChange={setIsPublishDraftsDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Alle Entwürfe veröffentlichen</DialogTitle>
                            <DialogDescription>
                                Diese Entwürfe werden für die ausgewählte Filiale {selectedStore?.label ? `(${selectedStore.label})` : ''} veröffentlicht.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-3">
                            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                                {draftPublishSummaries.length} Entwurf{draftPublishSummaries.length === 1 ? '' : 'e'} über alle Kalenderwochen
                            </div>

                            <div className="space-y-2">
                                {draftPublishSummaries.map(summary => {
                                    const typeLabel = summary.kind === 'Blocker'
                                        ? (BLOCKER_TYPE_LABELS[summary.blockerType ?? ''] ?? 'Blocker')
                                        : summary.kind;

                                    return (
                                        <div key={summary.entryId} className="rounded-md border px-3 py-2 text-sm">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="font-medium">{summary.practitionerName}</div>
                                                <div className="text-xs text-muted-foreground">{summary.categoryName}</div>
                                            </div>
                                            <div className="mt-1 text-muted-foreground">
                                                {typeLabel}: {formatDraftSchedule(summary)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setIsPublishDraftsDialogOpen(false)}
                                disabled={publishManyDraftsMutation.isPending}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handlePublishAllDrafts}
                                disabled={draftPublishSummaries.length === 0 || publishManyDraftsMutation.isPending}
                            >
                                {publishManyDraftsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Entwürfe veröffentlichen
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="font-medium">Legende:</span>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
                    <span>Verfügbarkeit (live)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-red-100 border border-red-300" />
                    <span>Blocker (live)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-gray-100 border border-green-500" />
                    <span>Verfügbarkeit (Entwurf)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-gray-100 border border-red-500" />
                    <span>Blocker (Entwurf)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
                    <span>Kategorie-Planung</span>
                </div>
            </div>
        </div>
    );
}

function DraftCategoryGroup({ group, isFirst, weekStart, colorIndex, preferencesMap, onEditEntry, onCreateEntry, categoryEntriesMap, onEditCategoryEntry, onCreateCategoryEntry }: {
    group: {
        categoryId: number | null;
        categoryName: string;
        practitioners: {
            id: number;
            firstName: string;
            lastName: string;
            title: string | null;
            category: { id: number; name: string } | null;
            days: {
                date: string;
                availabilities: { startTime: string; endTime: string; isDraft: boolean; entryId: string }[];
                blockers: { startTime: string; endTime: string; type: string | null; note: string | null; isDraft: boolean; entryId: string }[];
            }[];
        }[];
    };
    isFirst: boolean;
    weekStart: string;
    colorIndex: number;
    preferencesMap: Record<number, Record<string, { startTime: string; endTime: string; type: string }>>;
    onEditEntry: (entryId: string, date: string) => void;
    onCreateEntry: (practitionerId: number, date: string) => void;
    categoryEntriesMap: Record<number, Record<string, { startTime: string; endTime: string; entryId: string; description: string | null }[]>>;
    onEditCategoryEntry: (entryId: string, categoryId: number, categoryName: string, date: string) => void;
    onCreateCategoryEntry: (categoryId: number, categoryName: string, date: string) => void;
}) {
    const bgColor = CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length];
    const catEntries = group.categoryId ? categoryEntriesMap[group.categoryId] : undefined;

    const weekDates = useMemo(() => {
        const start = dayjs(weekStart);
        return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
    }, [weekStart]);

    return (
        <>
            <tr className={!isFirst ? 'border-t-2 border-gray-300' : ''}>
                <td colSpan={8} className={`px-4 py-2 ${bgColor}`}>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.categoryName}
                    </span>
                </td>
            </tr>
            {group.categoryId && group.categoryId !== 1 && group.categoryId !== 2 && (
                <tr className={`border-b border-gray-300 ${bgColor}`}>
                    <td className="px-4 py-2 text-sm font-medium whitespace-nowrap border-r border-gray-300 italic text-yellow-700">
                        {group.categoryName} (Planung)
                    </td>
                    {weekDates.map((date, i) => {
                        const dayEntries = catEntries?.[date] ?? [];
                        return (
                            <td key={i} className="px-1 py-2 text-center border-l border-gray-300 align-top">
                                <div className="flex flex-col gap-1 items-center">
                                    {dayEntries.map(entry => (
                                        <button
                                            key={entry.entryId}
                                            className="inline-flex flex-col items-center rounded px-2 py-1 border text-xs bg-yellow-100 border-yellow-300 text-yellow-800 cursor-pointer hover:bg-yellow-200 transition-colors"
                                            onClick={() => onEditCategoryEntry(entry.entryId, group.categoryId!, group.categoryName, date)}
                                        >
                                            <span className="font-medium">{entry.startTime} - {entry.endTime}</span>
                                            {entry.description && <span className="text-[10px] text-yellow-600">{entry.description}</span>}
                                        </button>
                                    ))}
                                    <button
                                        className="inline-flex items-center justify-center w-5 h-5 rounded border border-dashed border-yellow-400 text-yellow-600 hover:bg-yellow-100 hover:border-yellow-500 transition-colors cursor-pointer"
                                        onClick={() => onCreateCategoryEntry(group.categoryId!, group.categoryName, date)}
                                    >
                                        <Plus className="h-3 w-3" />
                                    </button>
                                </div>
                            </td>
                        );
                    })}
                </tr>
            )}
            {group.practitioners.map(practitioner => (
                <DraftPractitionerRow key={practitioner.id} practitioner={practitioner} weekStart={weekStart} bgColor={bgColor} preferencesMap={preferencesMap} onEditEntry={onEditEntry} onCreateEntry={onCreateEntry} categoryId={group.categoryId} />
            ))}
        </>
    );
}

function DraftPractitionerRow({ practitioner, weekStart, bgColor, preferencesMap, onEditEntry, onCreateEntry, categoryId }: {
    practitioner: {
        id: number;
        firstName: string;
        lastName: string;
        title: string | null;
        days: {
            date: string;
            availabilities: { startTime: string; endTime: string; isDraft: boolean; entryId: string }[];
            blockers: { startTime: string; endTime: string; type: string | null; note: string | null; isDraft: boolean; entryId: string }[];
        }[];
    };
    weekStart: string;
    bgColor: string;
    preferencesMap: Record<number, Record<string, { startTime: string; endTime: string; type: string }>>;
    onEditEntry: (entryId: string, date: string) => void;
    onCreateEntry: (practitionerId: number, date: string) => void;
    categoryId: number | null;
}) {
    const navigate = useNavigate();
    const displayName = [practitioner.title, practitioner.firstName, practitioner.lastName]
        .filter(Boolean)
        .join(' ');

    const linkBase = `/practitioner/${practitioner.id}?tab=availability&week=${weekStart}`;
    const practitionerPrefs = preferencesMap[practitioner.id];
    const weeklyAvailabilityMinutes = getWeeklyAvailabilityMinutes(practitioner.days);

    return (
        <tr className={`border-b border-gray-300 hover:brightness-95 transition-all ${bgColor}`}>
            <td className="px-4 py-2 text-sm font-medium whitespace-nowrap border-r border-gray-300">
                <div>{displayName}</div>
                <div className="text-xs font-normal text-muted-foreground">
                    {formatAvailabilityHours(weeklyAvailabilityMinutes)} Std. Verfügbarkeit
                </div>
            </td>
            {practitioner.days.map((day, i) => {
                const absenceBlockers = day.blockers.filter(b => b.type === 'VACATION' || b.type === 'SICK_LEAVE');

                const displayItems: { kind: 'available' | 'blocked'; startTime: string; endTime: string; isDraft: boolean; entryId: string; blockerType?: string | null }[] = [
                    ...day.availabilities.map(a => ({ kind: 'available' as const, startTime: a.startTime, endTime: a.endTime, isDraft: a.isDraft, entryId: a.entryId })),
                    ...absenceBlockers.map(b => ({ kind: 'blocked' as const, startTime: b.startTime, endTime: b.endTime, isDraft: b.isDraft, entryId: b.entryId, blockerType: b.type })),
                ];

                const preference = practitionerPrefs?.[day.date];
                const draftEntries = [
                    ...day.availabilities.filter(a => a.isDraft).map(a => ({ entryId: a.entryId, label: `Verfügbarkeit ${a.startTime} - ${a.endTime}` })),
                    ...absenceBlockers.filter(b => b.isDraft).map(b => ({ entryId: b.entryId, label: `${BLOCKER_TYPE_LABELS[b.type ?? ''] ?? 'Blockierung'} ${b.startTime} - ${b.endTime}` })),
                ];

                const isBlockedCategory = categoryId === 1 || categoryId === 2;

                const handleCellClick = () => {
                    if (draftEntries.length === 1) {
                        onEditEntry(draftEntries[0].entryId, day.date);
                    } else if (draftEntries.length === 0 && displayItems.length === 0) {
                        if (isBlockedCategory) {
                            navigate(linkBase);
                        } else {
                            onCreateEntry(practitioner.id, day.date);
                        }
                    } else if (draftEntries.length === 0) {
                        navigate(linkBase);
                    }
                };

                const cellContent = (
                    <>
                        <DraftDayCell items={displayItems} />
                        {preference && (
                            <div className={`text-[10px] mt-1 leading-tight ${preference.type === 'BLOCKED' ? 'text-red-600' : 'text-green-600'}`}>
                                {preference.type === 'BLOCKED' ? 'Abwesend' : 'Verfügbar'} {preference.startTime} - {preference.endTime}
                            </div>
                        )}
                    </>
                );

                return (
                    <td key={i} className="px-1 py-2 text-center border-l border-gray-300">
                        {draftEntries.length > 1 ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button className="block w-full min-h-[2rem] cursor-pointer text-left">
                                        {cellContent}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2" align="start">
                                    <div className="text-xs font-medium mb-1 text-muted-foreground">Entwurf auswählen:</div>
                                    <div className="flex flex-col gap-1">
                                        {draftEntries.map(entry => (
                                            <button
                                                key={entry.entryId}
                                                className="text-left text-xs px-2 py-1.5 rounded hover:bg-muted transition-colors"
                                                onClick={() => onEditEntry(entry.entryId, day.date)}
                                            >
                                                {entry.label}
                                            </button>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <button className="block w-full min-h-[2rem] cursor-pointer" onClick={handleCellClick}>
                                {cellContent}
                            </button>
                        )}
                    </td>
                );
            })}
        </tr>
    );
}

type DisplayItem = { kind: 'available' | 'blocked'; startTime: string; endTime: string; isDraft: boolean; entryId: string; blockerType?: string | null };

function DraftDayCell({ items }: { items: DisplayItem[] }) {
    if (items.length === 0) return null;

    return (
        <div className="flex flex-col gap-1 items-center">
            {items.map(item => (
                <EntryPill key={item.entryId} item={item} />
            ))}
        </div>
    );
}

function EntryPill({ item }: { item: DisplayItem }) {
    if (item.kind === 'available') {
        const colorClasses = item.isDraft
            ? 'bg-gray-100 border-green-500 text-gray-700'
            : 'bg-green-100 border-green-300 text-green-800';

        return (
            <div className={`inline-flex flex-col items-center rounded px-2 py-1 border text-xs ${colorClasses}`}>
                <span className="font-medium">
                    {item.startTime} - {item.endTime}
                </span>
            </div>
        );
    }

    const colorClasses = item.isDraft
        ? 'bg-gray-100 border-red-500 text-gray-700'
        : 'bg-red-100 border-red-300 text-red-800';

    const typeLabel = item.blockerType ? (BLOCKER_TYPE_LABELS[item.blockerType] ?? item.blockerType) : '';

    return (
        <div className={`inline-flex flex-col items-center rounded px-2 py-1 border text-xs ${colorClasses}`}>
            <span className="font-medium">
                {item.startTime} - {item.endTime}
            </span>
            {typeLabel && (
                <span className={`text-[10px] ${item.isDraft ? 'text-gray-500' : 'text-red-600'}`}>{typeLabel}</span>
            )}
        </div>
    );
}
